import { Box, Button, makeStyles, Typography } from "@material-ui/core";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { useEffect, useRef, useState } from "react";
import { DEFAULT_VALUE, useAddAssetContext } from "src/context/monitoring/addAssetContext";
import StyledTextField from "src/view/PoolMonitoring/Overview/AssetConfigOption/components/StyledTextField";
import ToggleButton from "src/view/PoolMonitoring/Overview/AssetConfigOption/components/ToggleButton";
import { useImmer } from "use-immer";
import TokenSelect from "./TokenSelect";

const useStyle = makeStyles((theme) => ({
  textRoot: {
    [theme.breakpoints.up("md")]: {
      width: "48%",
    },
  },
}));

const defaultConfig = { fullWidth: true, type: "number" };

export default function AddAssetForm({ index, assetId, data, setData, currentTokenList, ...props }) {
  const cls = useStyle();
  const mounted = useRef(false);
  const [value, setValue] = useImmer(DEFAULT_VALUE);
  const [collateral, setCollateral] = useState(false);
  const [helperText, setHelperText] = useImmer({
    loanToValue: null,
    lt: null,
    reserveFactor: null,
    liquidationBonus: null,
    utilizationOptimal: null,
    slope1: null,
    slope2: null,
    baseInterestRate: null,
    isValid: false,
  });
  const [isValid, setIsValid] = useState(false);
  const [helperTextAddress, setHelperTextAddress] = useState(null);
  const [selectedOracle, setSelectedOracle] = useState({ address: undefined, oracle: undefined });

  const { stepStatus } = useAddAssetContext();
  const disableAll = !(stepStatus.activeStep == 0);

  function validate(key) {
    const _value = value[key];
    if (_value === null || _value == undefined)
      setHelperText((draft) => {
        draft[key] = "This field is required";
      });
    else if (_value.length == 0)
      setHelperText((draft) => {
        draft[key] = "This field is required";
      });
    else {
      const _number = Number(_value);
      if (_number < 0)
        setHelperText((draft) => {
          draft[key] = "This field must be greater than or equal 0";
        });
      else if (["loanToValue", "lt", "liquidationBonus", "reserveFactor"].includes(key) && _number > 1) {
        setHelperText((draft) => {
          draft[key] = "This field must be less than or equal 1";
        });
      } else
        setHelperText((draft) => {
          draft[key] = null;
        });
    }
  }

  useEffect(() => {
    if (mounted.current) {
      validate("loanToValue");
      validate("lt");
      validate("reserveFactor");
      validate("liquidationBonus");
      validate("utilizationOptimal");
      validate("slope1");
      validate("slope2");
      validate("baseInterestRate");
      if (value.loanToValue > value.lt)
        setHelperText((draft) => {
          draft["loanToValue"] = "loan to value must be less than or equal lt";
          draft["lt"] = "loan to value must be less than or equal lt";
        });
      setHelperText((draft) => {
        draft["isValid"] = true;
      });
    }
    mounted.current = true;
  }, [value]);

  useEffect(() => {
    if (helperText["isValid"]) {
      const check =
        helperText["loanToValue"] === null &&
        helperText["lt"] === null &&
        helperText["reserveFactor"] === null &&
        helperText["liquidationBonus"] === null &&
        helperText["utilizationOptimal"] === null &&
        helperText["slope1"] === null &&
        helperText["slope2"] === null &&
        helperText["baseInterestRate"] === null &&
        helperTextAddress === null;
      setIsValid(check);
    }
  }, [helperText, helperTextAddress]);

  useEffect(() => {
    data[assetId] = {
      isValid: isValid,
      data: { ...value, oracle: selectedOracle, collateral },
    };
    setData({ ...data });
  }, [value, selectedOracle, helperText, collateral, isValid]);

  function onTextChange(key, data) {
    setValue((draft) => {
      draft[key] = data;
    });
  }
  return (
    <Box {...props}>
      <Box mb={1} display="flex" justifyContent="space-between" alignItems="baseline">
        <Typography variant="subtitle2">
          {value?.symbol == "" ? `Asset ${index + 1}` : `Asset ${index + 1}: ${value?.symbol}`}
        </Typography>
        <Box display="flex" alignItems="center">
          <Box display="flex" alignItems="center" mr={2}>
            <Typography className={cls.text2}>Collateral</Typography>
            <ToggleButton disable={disableAll} status={collateral} setStatus={setCollateral} />
          </Box>
          <Button
            variant="contained"
            size="small"
            style={{ background: "#7994C1", color: "white" }}
            onClick={() => {
              delete data[assetId];
              setData({ ...data });
            }}
            disabled={disableAll}
          >
            <DeleteOutlineIcon style={{ width: 20, height: 20 }} />
            <Typography variant="subtitle2" style={{ marginLeft: 8 }}>
              Delete
            </Typography>
          </Button>
        </Box>
      </Box>
      <TokenSelect
        mb={3}
        setValue={setValue}
        data={data}
        setHelperTextAddress={setHelperTextAddress}
        helperTextAddress={helperTextAddress}
        setSelectedOracle={setSelectedOracle}
        currentTokenList={currentTokenList}
      />
      {selectedOracle.address && (
        <Box mb={3}>
          <Typography variant="subtitle2">Oracles</Typography>
          <StyledTextField
            textProps={{
              label: selectedOracle.oracle,
              fullWidth: true,
              value: selectedOracle.address,
              inputProps: { readOnly: true },
            }}
          />
        </Box>
      )}
      <Box mb={2}>
        <Typography variant="subtitle2">Reserve Parameter</Typography>
        <Box display="flex" justifyContent="space-between" flexWrap="wrap">
          <StyledTextField
            textProps={{
              ...defaultConfig,
              label: "Loan to Value",
              value: value.loanToValue,
              error: helperText.loanToValue != null,
              helperText: helperText.loanToValue,
              onChange: (e) => onTextChange("loanToValue", e.target.value),
              disabled: disableAll,
            }}
            rootProps={{ mb: 2, className: cls.textRoot }}
            info="Something about Base LtV"
          />
          <StyledTextField
            textProps={{
              ...defaultConfig,
              label: "Liquidation Threshold",
              value: value.lt,
              error: helperText.lt != null,
              helperText: helperText.lt,
              disabled: disableAll,
              onChange: (e) => onTextChange("lt", e.target.value),
            }}
            rootProps={{ mb: 2, className: cls.textRoot }}
            info="Something about Base LT"
          />
        </Box>
        <Box display="flex" justifyContent="space-between" flexWrap="wrap" mb={2}>
          <StyledTextField
            textProps={{
              ...defaultConfig,
              label: "Reserve Factor",
              value: value.reserveFactor,
              error: helperText.reserveFactor != null,
              helperText: helperText.reserveFactor,
              disabled: disableAll,
              onChange: (e) => onTextChange("reserveFactor", e.target.value),
            }}
            rootProps={{ mb: 2, className: cls.textRoot }}
            info="Something about Base Reserve Factor"
          />
          <StyledTextField
            textProps={{
              ...defaultConfig,
              label: "Liquidation Bonus",
              value: value.liquidationBonus,
              error: helperText.liquidationBonus != null,
              helperText: helperText.liquidationBonus,
              disabled: disableAll,
              onChange: (e) => onTextChange("liquidationBonus", e.target.value),
            }}
            rootProps={{ mb: 2, className: cls.textRoot }}
            info="Something about Liquidation Bonus"
          />
        </Box>
      </Box>
      <Box>
        <Typography variant="subtitle2">Interest Rate Strategy</Typography>
        <Box display="flex" justifyContent="space-between" flexWrap="wrap">
          <StyledTextField
            textProps={{
              ...defaultConfig,
              label: "Slope 1",
              value: value.slope1,
              error: helperText.slope1 != null,
              helperText: helperText.slope1,
              disabled: disableAll,
              onChange: (e) => onTextChange("slope1", e.target.value),
            }}
            rootProps={{ mb: 2, className: cls.textRoot }}
            info="Something about Base Slope 1"
          />
          <StyledTextField
            textProps={{
              ...defaultConfig,
              label: "Slope 2",
              value: value.slope2,
              error: helperText.slope2 != null,
              helperText: helperText.slope2,
              disabled: disableAll,
              onChange: (e) => onTextChange("slope2", e.target.value),
            }}
            rootProps={{ mb: 2, className: cls.textRoot }}
            info="Something about Base Slope 2"
          />
        </Box>
        <Box display="flex" justifyContent="space-between" flexWrap="wrap">
          <StyledTextField
            textProps={{
              ...defaultConfig,
              label: "Utilization Optimal",
              value: value.utilizationOptimal,
              error: helperText.utilizationOptimal != null,
              helperText: helperText.utilizationOptimal,
              disabled: disableAll,
              onChange: (e) => onTextChange("utilizationOptimal", e.target.value),
            }}
            rootProps={{ mb: 2, className: cls.textRoot }}
            info="Something about Base Utilization Optimal"
          />
          <StyledTextField
            textProps={{
              ...defaultConfig,
              label: "Base Interest Rate",
              value: value.baseInterestRate,
              error: helperText.baseInterestRate != null,
              helperText: helperText.baseInterestRate,
              disabled: disableAll,
              onChange: (e) => onTextChange("baseInterestRate", e.target.value),
            }}
            rootProps={{ mb: 2, className: cls.textRoot }}
            info="Something about Base Interest Rate"
          />
        </Box>
      </Box>
    </Box>
  );
}
